/** Ticket passenger profiles — per-adult/student itineraries and pricing. */

const $ = (id) => document.getElementById(id);

export function paxCountFromForm() {
  return {
    adt: Number($('m_PaxAdt')?.value) || 0,
    stu: Number($('m_PaxStu')?.value) || 0,
    chd: Number($('m_PaxChd')?.value) || 0,
    inf: Number($('m_PaxInf')?.value) || 0,
  };
}

export function formatTicketPax(extra) {
  const p = extra?.pax;
  if (!p) return '';
  const parts = [];
  if (p.adt) parts.push(`${p.adt}ADT`);
  if (p.stu) parts.push(`${p.stu}STU`);
  if (p.chd) parts.push(`${p.chd}CHD`);
  if (p.inf) parts.push(`${p.inf}INF`);
  return parts.join(' ');
}

export function isPerAdtMode() {
  return !!$('perAdtItinerariesToggle')?.checked;
}

export function shouldOfferPerAdtMode() {
  return paxCountFromForm().adt >= 2;
}

function defaultProfile(seed = {}) {
  return {
    name: seed.name || null,
    cost: seed.cost ?? null,
    profit: seed.profit ?? null,
    trip_type: seed.trip_type || 'one_way',
    origin: seed.origin || '',
    destination: seed.destination || '',
    depart_date: seed.depart_date || '',
    return_date: seed.return_date || '',
    flight_segments: seed.flight_segments || [],
  };
}

export function travelerDisplayName(kind, index, profile = {}) {
  const custom = String(profile?.name || '').trim();
  if (custom) return custom;
  return kind === 'stu' ? `Student ${index + 1}` : `Adult ${index + 1}`;
}

function isSplitItineraryMode() {
  return isPerAdtMode() && shouldOfferPerAdtMode();
}

function tripTypeSegHtml(prefix, value) {
  const types = [
    ['one_way', 'One way'],
    ['return', 'Return'],
    ['multi', 'Multi-city'],
  ];
  return types.map(([v, label]) =>
    `<button type="button" class="mini-seg-btn${value === v ? ' active' : ''}" data-traveler-trip="${prefix}" data-value="${v}" aria-pressed="${value === v}">${label}</button>`
  ).join('');
}

function multiSegHtml(prefix, segIdx, seg, canRemove) {
  return `
    <div class="multi-city-seg traveler-multi-seg" data-traveler-seg="${segIdx}">
      <div class="multi-city-seg-head">
        <span class="multi-city-seg-num">Flight ${segIdx + 1}</span>
        ${canRemove ? `<button type="button" class="btn btn-ghost btn-sm traveler-remove-seg" data-traveler-prefix="${prefix}" data-seg="${segIdx}">Remove</button>` : ''}
      </div>
      <div class="route-airports">
        <div class="route-field"><label class="form-label">From</label><input class="form-input route-code traveler-seg-origin" data-prefix="${prefix}" maxlength="8" value="${escAttr(seg.origin)}"></div>
        <span class="route-arrow">→</span>
        <div class="route-field"><label class="form-label">To</label><input class="form-input route-code traveler-seg-dest" data-prefix="${prefix}" maxlength="8" value="${escAttr(seg.destination)}"></div>
      </div>
      <div class="route-dates"><div class="date-field"><label class="form-label">Depart</label><input type="date" class="form-input date-input traveler-seg-date" data-prefix="${prefix}" value="${escAttr(seg.depart_date)}"></div></div>
    </div>`;
}

function escAttr(v) {
  return String(v ?? '').replace(/"/g, '&quot;');
}

function travelerFlightProfileHtml(kind, index, profile = {}, opts = {}) {
  const p = defaultProfile(profile);
  const prefix = `${kind}${index}`;
  const defaultTitle = kind === 'stu' ? `Student ${index + 1}` : `Adult ${index + 1}`;
  const showPricing = opts.showPricing ?? false;
  const isMulti = p.trip_type === 'multi';
  const showReturn = p.trip_type === 'return';
  const segs = p.flight_segments?.length >= 2
    ? p.flight_segments
    : [{ origin: p.origin, destination: p.destination, depart_date: p.depart_date }, { origin: '', destination: '', depart_date: '' }];

  const pricingHtml = showPricing ? `
      <div class="traveler-pricing-row">
        <label class="pax-cell traveler-pricing-cost">
          <span class="pax-cell-label">Est. cost (LKR)</span>
          <input type="number" class="form-input traveler-cost" data-prefix="${prefix}" min="0" placeholder="0" value="${escAttr(p.cost ?? '')}" oninput="mysky.updatePaxQuoteTotal()">
        </label>
        <label class="pax-cell traveler-pricing-profit">
          <span class="pax-cell-label">Profit (LKR)</span>
          <input type="number" class="form-input traveler-profit" data-prefix="${prefix}" min="0" placeholder="0" value="${escAttr(p.profit ?? '')}" oninput="mysky.updatePaxQuoteTotal()">
        </label>
      </div>` : '';

  return `
    <div class="adt-flight-profile glass-sub traveler-flight-profile" data-traveler-kind="${kind}" data-traveler-index="${index}">
      <div class="adt-flight-profile-head">
        <label class="form-label traveler-name-label">Passenger name</label>
        <input type="text" class="form-input traveler-name" data-prefix="${prefix}" maxlength="80" placeholder="${defaultTitle}" value="${escAttr(p.name || '')}" aria-label="Name for ${defaultTitle}">
      </div>
      <div class="trip-type-row">
        <span class="form-label trip-type-label">Trip type</span>
        <div class="mini-seg trip-type-seg traveler-trip-seg" data-traveler-prefix="${prefix}" role="group">
          ${tripTypeSegHtml(prefix, p.trip_type)}
        </div>
      </div>
      <div class="traveler-simple-route${isMulti ? ' hidden' : ''}" data-traveler-route="${prefix}">
        <div class="route-airports">
          <div class="route-field"><label class="form-label">From</label><input class="form-input route-code traveler-origin" data-prefix="${prefix}" maxlength="8" value="${escAttr(p.origin)}" placeholder="CMB"></div>
          <span class="route-arrow">→</span>
          <div class="route-field"><label class="form-label">To</label><input class="form-input route-code traveler-dest" data-prefix="${prefix}" maxlength="8" value="${escAttr(p.destination)}" placeholder="DXB"></div>
        </div>
        <div class="route-dates">
          <div class="date-field"><label class="form-label">Depart</label><input type="date" class="form-input date-input traveler-depart" data-prefix="${prefix}" value="${escAttr(p.depart_date)}"></div>
          <div class="date-field traveler-return-wrap${showReturn ? '' : ' hidden'}" data-traveler-return-wrap="${prefix}">
            <label class="form-label">Return</label><input type="date" class="form-input date-input traveler-return" data-prefix="${prefix}" value="${escAttr(p.return_date)}">
          </div>
        </div>
      </div>
      <div class="traveler-multi-wrap${isMulti ? '' : ' hidden'}" data-traveler-multi="${prefix}">
        <div class="multi-city-header">
          <span class="form-label">Flights</span>
          <button type="button" class="btn btn-ghost btn-sm traveler-add-seg" data-prefix="${prefix}">+ Add flight</button>
        </div>
        <div class="traveler-multi-segments" data-traveler-segments="${prefix}">
          ${segs.map((s, si) => multiSegHtml(prefix, si, s, segs.length > 2)).join('')}
        </div>
      </div>
      ${pricingHtml}
    </div>`;
}

function bindTravelerProfileEvents() {
  document.querySelectorAll('.traveler-trip-seg .mini-seg-btn').forEach((btn) => {
    btn.onclick = () => {
      const prefix = btn.dataset.travelerTrip;
      const seg = btn.closest('.traveler-trip-seg');
      seg?.querySelectorAll('.mini-seg-btn').forEach((b) => {
        b.classList.toggle('active', b === btn);
        b.setAttribute('aria-pressed', b === btn ? 'true' : 'false');
      });
      onTravelerTripTypeChange(prefix, btn.dataset.value);
    };
  });
  document.querySelectorAll('.traveler-add-seg').forEach((btn) => {
    btn.onclick = () => addTravelerMultiSeg(btn.dataset.prefix);
  });
  document.querySelectorAll('.traveler-remove-seg').forEach((btn) => {
    btn.onclick = () => removeTravelerMultiSeg(btn.dataset.travelerPrefix, Number(btn.dataset.seg));
  });
}

function onTravelerTripTypeChange(prefix, tripType) {
  const profile = document.querySelector(`[data-traveler-route="${prefix}"]`)?.closest('.traveler-flight-profile');
  if (!profile) return;
  const isMulti = tripType === 'multi';
  const showReturn = tripType === 'return';
  profile.querySelector(`[data-traveler-route="${prefix}"]`)?.classList.toggle('hidden', isMulti);
  profile.querySelector(`[data-traveler-multi="${prefix}"]`)?.classList.toggle('hidden', !isMulti);
  profile.querySelector(`[data-traveler-return-wrap="${prefix}"]`)?.classList.toggle('hidden', !showReturn);
  if (!showReturn) {
    const ret = profile.querySelector(`.traveler-return[data-prefix="${prefix}"]`);
    if (ret) ret.value = '';
  }
}

function addTravelerMultiSeg(prefix) {
  const box = document.querySelector(`[data-traveler-segments="${prefix}"]`);
  if (!box) return;
  const rows = box.querySelectorAll('.traveler-multi-seg');
  if (rows.length >= 8) return;
  const last = rows[rows.length - 1];
  const lastDest = last?.querySelector('.traveler-seg-dest')?.value || '';
  const idx = rows.length;
  const div = document.createElement('div');
  div.innerHTML = multiSegHtml(prefix, idx, { origin: lastDest, destination: '', depart_date: '' }, true);
  box.appendChild(div.firstElementChild);
  bindTravelerProfileEvents();
}

function removeTravelerMultiSeg(prefix, segIdx) {
  const box = document.querySelector(`[data-traveler-segments="${prefix}"]`);
  if (!box) return;
  const rows = [...box.querySelectorAll('.traveler-multi-seg')];
  if (rows.length <= 2) return;
  rows[segIdx]?.remove();
  box.querySelectorAll('.traveler-multi-seg').forEach((row, i) => {
    row.dataset.travelerSeg = String(i);
    const num = row.querySelector('.multi-city-seg-num');
    if (num) num.textContent = `Flight ${i + 1}`;
  });
}

function seedFromSingleFlight() {
  return {
    trip_type: $('m_TripType')?.value || 'one_way',
    origin: $('m_Origin')?.value?.trim().toUpperCase() || '',
    destination: $('m_Destination')?.value?.trim().toUpperCase() || '',
    depart_date: $('m_Depart')?.value || '',
    return_date: $('m_Return')?.value || '',
  };
}

function buildProfileList(count, existing = [], seed = null) {
  if (existing.length === count) return existing;
  return Array.from({ length: count }, (_, i) => {
    if (existing[i]) return existing[i];
    if (i === 0 && seed) return defaultProfile(seed);
    return defaultProfile();
  });
}

function readTravelerFlightFromDom(kind, index) {
  const prefix = `${kind}${index}`;
  const profileEl = document.querySelector(`.traveler-flight-profile[data-traveler-kind="${kind}"][data-traveler-index="${index}"]`);
  if (!profileEl) return defaultProfile();

  const tripBtn = profileEl.querySelector(`.traveler-trip-seg[data-traveler-prefix="${prefix}"] .mini-seg-btn.active`);
  const trip_type = tripBtn?.dataset.value || 'one_way';

  if (trip_type === 'multi') {
    const segs = [...profileEl.querySelectorAll(`[data-traveler-segments="${prefix}"] .traveler-multi-seg`)].map((row) => ({
      origin: row.querySelector('.traveler-seg-origin')?.value.trim().toUpperCase() || '',
      destination: row.querySelector('.traveler-seg-dest')?.value.trim().toUpperCase() || '',
      depart_date: row.querySelector('.traveler-seg-date')?.value || '',
    }));
    return {
      trip_type,
      name: profileEl.querySelector(`.traveler-name[data-prefix="${prefix}"]`)?.value.trim() || null,
      cost: Number(profileEl.querySelector(`.traveler-cost[data-prefix="${prefix}"]`)?.value) || null,
      profit: Number(profileEl.querySelector(`.traveler-profit[data-prefix="${prefix}"]`)?.value) || null,
      origin: segs[0]?.origin || '',
      destination: segs[segs.length - 1]?.destination || '',
      depart_date: segs[0]?.depart_date || '',
      return_date: null,
      flight_segments: segs,
    };
  }

  return {
    trip_type,
    name: profileEl.querySelector(`.traveler-name[data-prefix="${prefix}"]`)?.value.trim() || null,
    cost: Number(profileEl.querySelector(`.traveler-cost[data-prefix="${prefix}"]`)?.value) || null,
    profit: Number(profileEl.querySelector(`.traveler-profit[data-prefix="${prefix}"]`)?.value) || null,
    origin: profileEl.querySelector(`.traveler-origin[data-prefix="${prefix}"]`)?.value.trim().toUpperCase() || '',
    destination: profileEl.querySelector(`.traveler-dest[data-prefix="${prefix}"]`)?.value.trim().toUpperCase() || '',
    depart_date: profileEl.querySelector(`.traveler-depart[data-prefix="${prefix}"]`)?.value || '',
    return_date: profileEl.querySelector(`.traveler-return[data-prefix="${prefix}"]`)?.value || '',
    flight_segments: [],
  };
}

export function collectAdtProfiles() {
  if (!isPerAdtMode()) return [];
  const adt = paxCountFromForm().adt;
  return Array.from({ length: adt }, (_, i) => readTravelerFlightFromDom('adt', i));
}

export function collectStuProfiles() {
  const stu = paxCountFromForm().stu;
  if (stu < 1) return [];
  return Array.from({ length: stu }, (_, i) => readTravelerFlightFromDom('stu', i));
}

function syncTravelerFlightBox(boxId, kind, count, existing = [], seed = null, showPricing = false) {
  const box = $(boxId);
  if (!box) return;
  if (count < 1 || (kind === 'adt' && !(isPerAdtMode() && count >= 2))) {
    box.classList.add('hidden');
    box.innerHTML = '';
    return;
  }
  box.classList.remove('hidden');
  const pricingKey = showPricing ? '1' : '0';
  const current = box.querySelectorAll('.traveler-flight-profile').length;
  if (current !== count || box.dataset.showPricing !== pricingKey) {
    box.dataset.showPricing = pricingKey;
    const profiles = buildProfileList(count, existing.length === count ? existing : [], seed);
    box.innerHTML = profiles.map((p, i) => travelerFlightProfileHtml(kind, i, p, { showPricing })).join('');
    bindTravelerProfileEvents();
  }
}

export function syncPerAdtUi() {
  const { adt, stu } = paxCountFromForm();
  const offerSplit = shouldOfferPerAdtMode();
  const split = isSplitItineraryMode();
  const seed = seedFromSingleFlight();

  $('perAdtToggleWrap')?.classList.toggle('hidden', !offerSplit);
  if (!offerSplit && $('perAdtItinerariesToggle')) {
    $('perAdtItinerariesToggle').checked = false;
  }

  $('singleFlightWrap')?.classList.toggle('hidden', split);

  $('aggregateAdtRateCell')?.classList.toggle('hidden', split);
  $('aggregateStuRateCell')?.classList.toggle('hidden', split);
  $('aggregateProfitAdtCell')?.classList.toggle('hidden', split);
  $('aggregateProfitStuCell')?.classList.toggle('hidden', split);
  $('aggregateRatesRow')?.classList.remove('hidden');

  const hint = $('ticketQuoteHint');
  if (hint) {
    hint.textContent = split
      ? 'Enter cost and profit on each passenger card — CHD/INF use the shared rates below.'
      : 'Fill after you find a matching flight — totals calculate automatically.';
  }

  const existingAdtFlights = split ? collectAdtProfiles() : [];
  syncTravelerFlightBox('adtFlightProfiles', 'adt', adt, existingAdtFlights, seed, split);

  const existingStuFlights = stu >= 1 ? collectStuProfiles() : [];
  syncTravelerFlightBox('stuFlightProfiles', 'stu', stu, existingStuFlights, seed, split);
}

function profitRatesFromForm() {
  return {
    adt: Number($('m_ProfitAdt')?.value) || 0,
    stu: Number($('m_ProfitStu')?.value) || 0,
    chd: Number($('m_ProfitChd')?.value) || 0,
    inf: Number($('m_ProfitInf')?.value) || 0,
  };
}

function mergeProfilePricing(profile, pricing, index) {
  return {
    ...profile,
    name: profile?.name || pricing?.name || null,
    cost: profile?.cost ?? pricing?.cost ?? null,
    profit: profile?.profit ?? pricing?.profit ?? null,
  };
}

export function setAdtProfiles(extra) {
  const perAdt = !!extra?.per_adt_itineraries && Array.isArray(extra?.adt_profiles) && extra.adt_profiles.length >= 2;
  if ($('perAdtItinerariesToggle')) $('perAdtItinerariesToggle').checked = perAdt;
  syncPerAdtUi();

  const { stu } = paxCountFromForm();
  const split = perAdt;
  if (perAdt && extra.adt_profiles?.length && $('adtFlightProfiles')) {
    const profiles = extra.adt_profiles.map((p, i) => mergeProfilePricing(p, extra.adt_pricing?.[i], i));
    $('adtFlightProfiles').innerHTML = profiles.map((p, i) => travelerFlightProfileHtml('adt', i, p, { showPricing: split })).join('');
    bindTravelerProfileEvents();
  }
  if (stu >= 1 && extra.stu_profiles?.length && $('stuFlightProfiles')) {
    const profiles = extra.stu_profiles.map((p, i) => mergeProfilePricing(p, extra.stu_pricing?.[i], i));
    $('stuFlightProfiles').innerHTML = profiles.map((p, i) => travelerFlightProfileHtml('stu', i, p, { showPricing: split })).join('');
    bindTravelerProfileEvents();
  }
  updatePaxQuoteTotalFromDom();
}

function updatePaxQuoteTotalFromDom() {
  if (typeof window !== 'undefined' && window.mysky?.updatePaxQuoteTotal) {
    window.mysky.updatePaxQuoteTotal();
  }
}

export function calcTicketPricingBreakdown() {
  const pax = paxCountFromForm();
  const adtBase = Number($('m_RateAdt')?.value) || 0;
  const stuBase = Number($('m_RateStu')?.value) || 0;
  const chdBase = Number($('m_RateChd')?.value) || 0;
  const infBase = Number($('m_RateInf')?.value) || 0;
  const profit = profitRatesFromForm();
  let cost = 0;
  let profitTotal = 0;
  let hasValue = false;

  const addAmount = (amountCost, amountProfit) => {
    if (amountCost > 0 || amountProfit > 0) {
      cost += amountCost;
      profitTotal += amountProfit;
      hasValue = true;
    }
  };

  const addType = (count, base, typeProfit) => {
    if (!count) return;
    if (base > 0 || typeProfit > 0) {
      addAmount(count * base, count * typeProfit);
    }
  };

  if (isSplitItineraryMode()) {
    collectAdtProfiles().forEach((pr) => addAmount(Number(pr.cost) || 0, Number(pr.profit) || 0));
    collectStuProfiles().forEach((pr) => addAmount(Number(pr.cost) || 0, Number(pr.profit) || 0));
    addType(pax.chd, chdBase, profit.chd);
    addType(pax.inf, infBase, profit.inf);
  } else {
    addType(pax.adt, adtBase, profit.adt);
    addType(pax.stu, stuBase, profit.stu);
    addType(pax.chd, chdBase, profit.chd);
    addType(pax.inf, infBase, profit.inf);
  }

  if (!hasValue) return null;
  const roundedCost = Math.round(cost);
  const roundedProfit = Math.round(profitTotal);
  const total = roundedCost + roundedProfit;
  return {
    cost: roundedCost,
    profit: roundedProfit,
    total: total > 0 ? total : null,
  };
}

export function calcTicketCostTotal() {
  return calcTicketPricingBreakdown()?.total ?? null;
}

export function applyAdtProfilesToBody(body) {
  if (body.type !== 'ticket') return;
  const profiles = body.extra?.adt_profiles;
  if (!body.extra?.per_adt_itineraries || !profiles?.length) return;
  const first = profiles[0];
  body.trip_type = first.trip_type || body.trip_type;
  body.origin = first.origin || null;
  body.destination = first.destination || null;
  body.depart_date = first.depart_date || null;
  body.return_date = first.trip_type === 'return' ? (first.return_date || null) : null;
  if (first.trip_type === 'multi' && first.flight_segments?.length) {
    body.extra.flight_segments = first.flight_segments;
  }
}

function validateTravelerProfiles(profiles, labelPrefix) {
  for (let i = 0; i < profiles.length; i += 1) {
    const pr = profiles[i];
    const kind = labelPrefix === 'Student' ? 'stu' : 'adt';
    const label = travelerDisplayName(kind, i, pr);
    if (pr.trip_type === 'multi') {
      const segs = pr.flight_segments || [];
      for (let j = 0; j < segs.length; j += 1) {
        if (!segs[j].origin || !segs[j].destination) return `Enter from and to for ${label}, flight ${j + 1}`;
        if (!segs[j].depart_date) return `Enter date for ${label}, flight ${j + 1}`;
      }
      continue;
    }
    if (!pr.origin || !pr.destination) return `Enter from and to for ${label}`;
    if (!pr.depart_date) return `Enter departure date for ${label}`;
    if (pr.trip_type === 'return') {
      if (!pr.return_date) return `Enter return date for ${label}`;
      if (pr.return_date < pr.depart_date) return `Return must be on or after depart for ${label}`;
    }
  }
  return null;
}

export function validateAdtProfiles(body) {
  if (body.type !== 'ticket') return null;
  if (body.extra?.per_adt_itineraries) {
    const err = validateTravelerProfiles(body.extra.adt_profiles || [], 'Adult');
    if (err) return err;
  }
  if (body.extra?.stu_profiles?.length) {
    const err = validateTravelerProfiles(body.extra.stu_profiles, 'Student');
    if (err) return err;
  }
  return null;
}

export function buildAdtExtraFields(extra, type) {
  if (type !== 'ticket') {
    extra.per_adt_itineraries = null;
    extra.adt_profiles = null;
    extra.stu_profiles = null;
    extra.adt_pricing = null;
    extra.stu_pricing = null;
    return;
  }

  const { stu } = paxCountFromForm();
  extra.adt_pricing = null;
  extra.stu_pricing = null;

  if (isPerAdtMode() && shouldOfferPerAdtMode()) {
    extra.per_adt_itineraries = true;
    extra.adt_profiles = collectAdtProfiles();
    extra.stu_profiles = stu >= 1 ? collectStuProfiles() : null;
    extra.adt_pricing = extra.adt_profiles.map((p) => ({
      name: p.name || null,
      cost: p.cost ?? null,
      profit: p.profit ?? null,
    }));
    extra.stu_pricing = (extra.stu_profiles || []).map((p) => ({
      name: p.name || null,
      cost: p.cost ?? null,
      profit: p.profit ?? null,
    }));
  } else {
    extra.per_adt_itineraries = null;
    extra.adt_profiles = null;
    extra.stu_profiles = stu >= 1 ? collectStuProfiles() : null;
  }
}

export function initTicketPaxUi() {
  $('perAdtItinerariesToggle')?.addEventListener('change', () => syncPerAdtUi());
  document.querySelectorAll('.inquiry-card-collapsible .inquiry-card-toggle').forEach((btn) => {
    btn.addEventListener('click', () => {
      const card = btn.closest('.inquiry-card-collapsible');
      const open = card?.classList.toggle('is-open');
      btn.setAttribute('aria-expanded', open ? 'true' : 'false');
    });
  });
}

export function resetInquiryCardCollapse({ openClient = true, openTrip = false, openAll = false } = {}) {
  document.querySelectorAll('.inquiry-card-collapsible').forEach((card, i) => {
    const open = openAll || (openClient && i === 0) || (openTrip && i === 1);
    card.classList.toggle('is-open', open);
    card.querySelector('.inquiry-card-toggle')?.setAttribute('aria-expanded', open ? 'true' : 'false');
  });
}

export function formatAdtItinerarySummary(extra) {
  const parts = [];
  if (extra?.per_adt_itineraries && extra?.adt_profiles?.length) {
    parts.push(...extra.adt_profiles.map((pr, i) => formatTravelerRoute(travelerDisplayName('adt', i, pr), pr)));
  }
  if (extra?.stu_profiles?.length) {
    parts.push(...extra.stu_profiles.map((pr, i) => formatTravelerRoute(travelerDisplayName('stu', i, pr), pr)));
  }
  return parts.join(' | ');
}

function formatTravelerRoute(prefix, pr) {
  const route = pr.trip_type === 'multi' && pr.flight_segments?.length
    ? pr.flight_segments.map((s) => `${s.origin || '?'}→${s.destination || '?'}`).join(' · ')
    : `${pr.origin || '?'}→${pr.destination || '?'}`;
  const tt = pr.trip_type === 'return' ? ' return' : pr.trip_type === 'multi' ? ' multi' : '';
  return `${prefix}: ${route}${tt}`;
}
